import UserAvatar from "@/components/avatar/user-avatar";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
// import NotificationButton from "@/components/notification/NotificationButton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import Hstack from "@/components/ui/hstack";
import { useChangeEmailPassword, useSignOut } from "@/hook/useAuth";
import { AvatarIcon } from "@/shared/icons";
import React from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { Input2 } from "@/components/ui/input2";
import { XIcon } from "lucide-react";
import { IconInfoCircle } from "@tabler/icons-react";
import { useUserStore } from "@/store/useUser";
import { LoginType } from "@/shared/constants/enum";

interface ChangePasswordFormValues {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const changePasswordSchema = z
  .object({
    oldPassword: z.string().min(1, "Old password is required."),
    newPassword: z.string().min(1, "New password is required."),
    confirmPassword: z.string().min(1, "Confirm Password is required."),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Confirm Password is not correct. Please check again.",
    path: ["confirmPassword"],
  });

export default function NotifyUser() {
  const { mutate: signOut } = useSignOut();
  const [isChangeEmailOpen, setIsChangeEmailOpen] = React.useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = React.useState(false);
  const [newEmail, setNewEmail] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [serverEmailError, setServerEmailError] = React.useState<
    string | undefined
  >(undefined);

  const { user, setUser, accountType } = useUserStore();
  const { mutateAsync: changeEmailPassword } = useChangeEmailPassword();

  const {
    register: registerChangePassword,
    handleSubmit: handleSubmitChangePasswordForm,
    formState: { errors: changePasswordErrors },
    setError: setChangePasswordError,
    reset: resetChangePasswordForm,
  } = useForm<ChangePasswordFormValues>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: {
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    mode: "onSubmit",
  });

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValid =
    newEmail.length > 0 &&
    emailRegex.test(newEmail) &&
    newEmail !== user?.email;
  const newEmailError =
    newEmail.length === 0
      ? undefined
      : !emailRegex.test(newEmail)
      ? "Enter a valid email address."
      : newEmail === user?.email
      ? "Email already exists in the system. Please use another email."
      : undefined;

  const handleSubmitChangeEmail = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!isValid) return;
    try {
      setSubmitting(true);
      setServerEmailError(undefined);
      await changeEmailPassword({
        new_email: newEmail,
      });
      toast.success("Email change successfully", {
        duration: 5000,
        position: "top-right",
      });
      if (user) {
        setUser({
          ...user,
          email: newEmail,
        });
      }
      setIsChangeEmailOpen(false);
      setNewEmail("");
    } catch (error) {
      const anyError = error as any;
      const message =
        anyError?.response?.data?.message ||
        anyError?.response?.data?.error ||
        anyError?.response?.data?.detail ||
        anyError?.message;

      setServerEmailError(message);

      if (!message) {
        toast.error("Failed to change email", {
          duration: 5000,
          position: "top-right",
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitChangePassword = async (
    values: ChangePasswordFormValues
  ) => {
    try {
      setSubmitting(true);
      if (values.newPassword === values.oldPassword) {
        setChangePasswordError("newPassword", {
          type: "server",
          message:
            "New password is same as old password. Please enter a new one.",
        });
        setSubmitting(false);
        return;
      }
      await changeEmailPassword({
        old_password: values.oldPassword,
        new_password: values.newPassword,
      });
      toast.success("Password changed successfully", {
        duration: 5000,
        position: "top-right",
      });
      setIsChangePasswordOpen(false);
      resetChangePasswordForm();
    } catch (error) {
      const anyError = error as any;
      const message: string | undefined =
        anyError?.response?.data?.message ||
        anyError?.response?.data?.error ||
        anyError?.response?.data?.detail ||
        anyError?.message;

      if (message) {
        // Map backend error message to specific fields similar to register-form
        if (message.includes("Old password is incorrect. Please try again.")) {
          setChangePasswordError("oldPassword", {
            type: "server",
            message: "Password is not correct.",
          });
        } else if (
          message.includes(
            "New password is same as old password. Please enter a new one."
          )
        ) {
          setChangePasswordError("newPassword", {
            type: "server",
            message,
          });
        } else if (
          message.includes(
            "Confirm Password is not correct. Please check again."
          )
        ) {
          setChangePasswordError("confirmPassword", {
            type: "server",
            message,
          });
        } else {
          toast.error(message, {
            duration: 5000,
            position: "top-right",
          });
        }
      } else {
        toast.error("Failed to change password", {
          duration: 5000,
          position: "top-right",
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Hstack gap={"12px"} align={"center"}>
      {/* <NotificationButton /> */}
      {user?.email && (
        <span className="hidden md:block max-w-[220px] truncate text-sm font-medium leading-none text-white/90">
          {user.email}
        </span>
      )}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <button type="button" className="cursor-pointer">
            <UserAvatar fallback={<AvatarIcon />} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {accountType == LoginType.MANUAL && (
            <>
              <DropdownMenuItem
                className="cursor-pointer hover:bg-gray-100"
                onClick={() => {
                  setIsChangeEmailOpen(true);
                }}
              >
                Change Email
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer hover:bg-gray-100"
                onClick={() => {
                  setIsChangePasswordOpen(true);
                }}
              >
                Change Password
              </DropdownMenuItem>
            </>
          )}

          <DropdownMenuItem
            className="cursor-pointer hover:bg-gray-100"
            onClick={() => signOut()}
          >
            Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
        <Dialog
          open={isChangeEmailOpen}
          onOpenChange={(open) => {
            setIsChangeEmailOpen(open);
            if (!open) setNewEmail("");
          }}
        >
          <DialogContent
            className="md:max-w-lg lg:max-w-xl"
            showCloseButton={false}
          >
            <DialogHeader className="flex-row items-center justify-between text-left">
              <DialogTitle className="text-xl font-normal">
                Change Email
              </DialogTitle>
              <DialogClose className="focus:ring-ring rounded-md p-2 opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden">
                <XIcon className="size-5" />
                <span className="sr-only">Close</span>
              </DialogClose>
            </DialogHeader>

            <div className="-mx-6 border-t" />

            <div className="bg-muted/10 rounded-md text-sm">
              <div className="flex items-center gap-2">
                <IconInfoCircle className="mt-0 size-6" />
                <DialogDescription className="m-0 text-center text-black">
                  Changing email is permanent.
                </DialogDescription>
              </div>
            </div>

            <form className="grid gap-4" onSubmit={handleSubmitChangeEmail}>
              <div className="grid gap-1.5">
                <Label
                  htmlFor="current-email"
                  className="text-[14px] font-normal"
                >
                  Current email
                </Label>
                <Input2
                  id="current-email"
                  value={user?.email || ""}
                  disabled
                  aria-label="Current email"
                />
              </div>

              <div className="grid gap-1.5">
                <Label
                  className="gap-0 text-[14px] font-normal"
                  htmlFor="new-email"
                >
                  New email<span className="text-destructive">*</span>
                </Label>
                <Input2
                  id="new-email"
                  type="email"
                  placeholder="Enter new email..."
                  value={newEmail}
                  onChange={(e) => {
                    setNewEmail(e.target.value);
                    setServerEmailError(undefined);
                  }}
                  aria-invalid={
                    newEmailError || serverEmailError ? true : undefined
                  }
                />
                {serverEmailError ? (
                  <span className="text-destructive text-xs">
                    {serverEmailError}
                  </span>
                ) : newEmailError ? (
                  <span className="text-destructive text-xs">
                    {newEmailError}
                  </span>
                ) : null}
              </div>

              <div className="-mx-6 border-t" />

              <DialogFooter className="pt-2">
                <div className="grid w-full grid-cols-2 gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => setIsChangeEmailOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={!isValid || submitting}
                    className="w-full"
                  >
                    {submitting ? "Saving..." : "Save"}
                  </Button>
                </div>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        <Dialog
          open={isChangePasswordOpen}
          onOpenChange={(open) => {
            setIsChangePasswordOpen(open);
            if (!open) {
              resetChangePasswordForm();
            }
          }}
        >
          <DialogContent
            className="md:max-w-lg lg:max-w-xl"
            showCloseButton={false}
          >
            <DialogHeader className="flex-row items-center justify-between text-left">
              <DialogTitle className="text-xl font-normal">
                Change Password
              </DialogTitle>
              <DialogClose className="focus:ring-ring rounded-md p-2 opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden">
                <XIcon className="size-5" />
                <span className="sr-only">Close</span>
              </DialogClose>
            </DialogHeader>

            <div className="-mx-6 border-t" />

            <div className="bg-muted/10 rounded-md text-sm">
              <div className="flex items-center gap-2">
                <IconInfoCircle className="mt-0 size-6" />
                <DialogDescription className="m-0 text-center text-black">
                  Changing password is permanent.
                </DialogDescription>
              </div>
            </div>

            <form
              className="grid gap-4"
              onSubmit={handleSubmitChangePasswordForm(
                handleSubmitChangePassword
              )}
            >
              <div className="grid gap-1.5">
                <Label
                  htmlFor="old-password"
                  className="gap-0 text-[14px] font-normal"
                >
                  Old password<span className="text-destructive">*</span>
                </Label>
                <PasswordInput
                  id="old-password"
                  placeholder="Enter old password..."
                  {...registerChangePassword("oldPassword")}
                  aria-invalid={
                    changePasswordErrors.oldPassword ? true : undefined
                  }
                />
                {changePasswordErrors.oldPassword ? (
                  <span className="text-destructive text-xs">
                    {changePasswordErrors.oldPassword.message}
                  </span>
                ) : null}
              </div>

              <div className="grid gap-1.5">
                <Label
                  className="gap-0 text-[14px] font-normal"
                  htmlFor="new-password"
                >
                  New password<span className="text-destructive">*</span>
                </Label>
                <PasswordInput
                  id="new-password"
                  placeholder="Enter new password..."
                  {...registerChangePassword("newPassword")}
                  aria-invalid={
                    changePasswordErrors.newPassword ? true : undefined
                  }
                />
                {changePasswordErrors.newPassword ? (
                  <span className="text-destructive text-xs">
                    {changePasswordErrors.newPassword.message}
                  </span>
                ) : null}
              </div>

              <div className="grid gap-1.5">
                <Label
                  className="gap-0 text-[14px] font-normal"
                  htmlFor="new-password"
                >
                  Confirm Password<span className="text-destructive">*</span>
                </Label>
                <PasswordInput
                  id="confirm-password"
                  placeholder="Enter password"
                  {...registerChangePassword("confirmPassword")}
                  aria-invalid={
                    changePasswordErrors.confirmPassword ? true : undefined
                  }
                />
                {changePasswordErrors.confirmPassword ? (
                  <span className="text-destructive text-xs">
                    {changePasswordErrors.confirmPassword.message}
                  </span>
                ) : null}
              </div>

              <div className="-mx-6 border-t" />

              <DialogFooter className="pt-2">
                <div className="grid w-full grid-cols-2 gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setIsChangePasswordOpen(false);
                      resetChangePasswordForm();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full"
                  >
                    {submitting ? "Saving..." : "Save"}
                  </Button>
                </div>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </DropdownMenu>
    </Hstack>
  );
}
